import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, UrlSegment } from '@angular/router';
import { ViewportService } from '@core/services/viewport.service';

function isAllowedPath(segments: UrlSegment[], allowedPaths: readonly string[]): boolean {
  const firstPath = segments[0]?.path ?? '';
  return allowedPaths.includes(firstPath);
}

export function mobilePathsMatch(...allowedPaths: string[]): CanMatchFn {
  return (_route, segments) => {
    const viewport = inject(ViewportService);
    return viewport.isMobile() && isAllowedPath(segments, allowedPaths);
  };
}

export const mobileOnlyMatch: CanMatchFn = () => {
  const viewport = inject(ViewportService);
  return viewport.isMobile();
};

/**
 * Redirects to `/login` (with original query params preserved). Used as a
 * `canActivate` on the mobile root path because Angular forbids combining
 * `canMatch` + `redirectTo` on the same route, but we still need a viewport-
 * aware redirect so the mobile login URL key (`/login`) stays distinct from
 * the tabs-shell parent URL key (`/`). Keeping those distinct prevents
 * Ionic's IonRouterOutlet from reusing the cached login view after post-login
 * navigation — the bug where the URL flipped to `/aufgaben` but the login
 * page stayed visible.
 */
export const mobileRootToLoginRedirect: CanActivateFn = (_route, state) => {
  const router = inject(Router);
  const queryParams = parseQueryParams(state.url);
  return router.createUrlTree(['/login'], { queryParams });
};

function parseQueryParams(url: string): Record<string, string> {
  const queryIdx = url.indexOf('?');
  if (queryIdx === -1) return {};
  const params: Record<string, string> = {};
  for (const pair of url.slice(queryIdx + 1).split('&')) {
    if (!pair) continue;
    const [key, value = ''] = pair.split('=');
    if (key) params[decodeURIComponent(key)] = decodeURIComponent(value);
  }
  return params;
}
